import { memo, useCallback, useMemo } from "react";

import DebouncedFrame from "metabase/common/components/DebouncedFrame";
import { DetailViewSidesheet } from "metabase/detail-view/components";
import * as Urls from "metabase/lib/urls";
import QueryVisualization from "metabase/query_builder/components/QueryVisualization";
import { Flex } from "metabase/ui";
import { getMode } from "metabase/visualizations/click-actions/lib/modes";
import type Question from "metabase-lib/v1/Question";
import type { Card, VisualizationSettings } from "metabase-types/api";

import { useResultObjectDetail } from "../hooks/use-result-object-detail";
import type { AdhocResult } from "../types";

import { ResultFooter } from "./ResultFooter";
import S from "./ResultPanel.module.css";
import type { ResultSidebarPanel } from "./ResultSidebar";

export type ResultPanelProps = {
  result: AdhocResult;
  /** The question as executed — derived once by the tab panel and shared. */
  question: Question;
  /** Whether this panel belongs to the tab the user is looking at. */
  isActive: boolean;
  isShowingRawTable: boolean;
  chartPanel: ResultSidebarPanel | null;
  onToggleRawTable: (isShowingRawTable: boolean) => void;
  onToggleChartPanel: (panel: ResultSidebarPanel) => void;
  onSelectVisualization: (question: Question) => void;
  onChangeVisualizationSettings: (settings: VisualizationSettings) => void;
};

/**
 * One historical result.
 *
 * The question is derived from the result's own snapshot rather than from the
 * editor, so editing the SQL afterwards can't change what this panel renders or
 * exports, and switching this result to a bar chart leaves its siblings alone.
 */
function ResultPanelInner({
  result,
  question,
  isActive,
  isShowingRawTable,
  chartPanel,
  onToggleRawTable,
  onToggleChartPanel,
  onSelectVisualization,
  onChangeVisualizationSettings,
}: ResultPanelProps) {
  const data = result.dataset?.data ?? null;
  const objectDetail = useResultObjectDetail(data, isActive);

  const rawSeries = useMemo(() => {
    if (!result.dataset?.data) {
      return null;
    }
    const card = question.card();
    // Mirrors `getRawSeries` in the query builder: the data/visualization
    // toggle swaps the rendered card to a plain table without touching the
    // result's own display setting.
    return [
      {
        ...result.dataset,
        card: isShowingRawTable
          ? {
              ...card,
              display: "table" as const,
              visualization_settings: {
                ...card.visualization_settings,
                "table.pivot": false,
                "table.column_formatting": [],
                column_settings: {},
              },
            }
          : card,
      },
    ];
  }, [isShowingRawTable, question, result.dataset]);

  const handleUpdateVisualizationSettings = useCallback(
    (settings: VisualizationSettings) => {
      onChangeVisualizationSettings({
        ...result.visualizationSettings,
        ...settings,
      });
    },
    [onChangeVisualizationSettings, result.visualizationSettings],
  );

  const mode = useMemo(() => getMode(question)?.queryMode(), [question]);

  /**
   * Drilling to a *different* question can't happen in place here — the tab
   * owns a native query the drill knows nothing about — so send it to the
   * query builder in a new browser tab and leave the workbench as it was.
   *
   * `Visualization` also treats this prop as the switch that mounts the click
   * actions popover at all, which is what surfaces the column header menu
   * (sort, freeze) on the result table.
   */
  const handleChangeCardAndRun = useCallback(
    ({ nextCard }: { nextCard: Card }) => {
      window.open(Urls.question(null, { hash: nextCard }), "_blank");
    },
    [],
  );

  return (
    <Flex
      className={S.panel}
      direction="column"
      data-testid="adhoc-result-panel"
    >
      <Flex className={S.body}>
        <DebouncedFrame className={S.frame}>
          <QueryVisualization
            question={question}
            result={result.dataset}
            rawSeries={rawSeries}
            mode={mode}
            zoomedRowIndex={
              objectDetail.rowIndex >= 0 ? objectDetail.rowIndex : undefined
            }
            isRunnable={false}
            isRunning={result.status === "running"}
            isDirty={false}
            // The "results are stale, re-run" overlay belongs to the editor,
            // not to a result that is by definition a snapshot.
            isResultDirty={false}
            isNativeEditorOpen={false}
            runQuestionQuery={() => Promise.resolve()}
            cancelQuery={() => undefined}
            navigateToNewCardInsideQB={handleChangeCardAndRun}
            onUpdateQuestion={onSelectVisualization}
            onOpenChartSettings={() => onToggleChartPanel("chart-settings")}
            onUpdateVisualizationSettings={handleUpdateVisualizationSettings}
          />
        </DebouncedFrame>
      </Flex>
      <ResultFooter
        result={result}
        question={question}
        rawSeries={rawSeries}
        openSidebarPanel={chartPanel}
        isShowingRawTable={isShowingRawTable}
        onToggleSidebarPanel={onToggleChartPanel}
        onToggleRawTable={onToggleRawTable}
      />
      {isActive && data != null && objectDetail.row != null && (
        <DetailViewSidesheet
          columns={data.cols}
          columnSettings={result.visualizationSettings["table.columns"]}
          columnsSettings={data.cols.map(() => undefined)}
          // Native results have no filterable query behind them, so there is
          // no row-level query to hand the sidesheet.
          query={undefined}
          row={objectDetail.row}
          rowId={objectDetail.rowIndex}
          showImplicitActions={false}
          showNav={data.rows.length > 1}
          table={undefined}
          url={undefined}
          onClose={objectDetail.close}
          onNextClick={
            objectDetail.rowIndex < data.rows.length - 1
              ? () => objectDetail.zoomToRow(objectDetail.rowIndex + 1)
              : undefined
          }
          onPreviousClick={
            objectDetail.rowIndex > 0
              ? () => objectDetail.zoomToRow(objectDetail.rowIndex - 1)
              : undefined
          }
        />
      )}
    </Flex>
  );
}

export const ResultPanel = memo(ResultPanelInner);
